import React, { useState, useEffect } from "react";
import ActionBar from "../../common/ActionBar";
import {
  makeStyles,
  AppBar,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import Rename from "./component/Rename";
import _ from "lodash";
import { navigate } from "@reach/router";
import BulkUpload from "../../internal/BulkUpload";
import BulkDownload from "../../internal/BulkDownload";
import SplitAndUpload from "./component/SplitAndUpload";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import CaseService from "../../../services/CaseService";
import SplitAndDownload from "./component/SplitAndDownload";
import PropTypes from "prop-types";
import { Icon } from "../PaymentHistoryPage";
import styled from "styled-components";
import PartyService from "../../../services/PartyService";
import useUser from "../../../hooks/useUser";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <StyledText style={{ height: "100%" }}>{children}</StyledText>
      )}
    </div>
  );
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 48,
  width: "100%",
  borderRadius: 12,
  background:
    "linear-gradient(90deg,rgba(42, 123, 155, 1) 0%, rgba(87, 199, 133, 1) 50%, rgba(237, 221, 83, 1) 100%)",
  boxShadow: "0 4px 15px rgba(101, 41, 255, 0.3)",
  "& .MuiTabs-indicator": {
    height: 4,
    borderRadius: 4,
    background: "linear-gradient(90deg, #ffffff , #ffffff)",
    transition: "all 0.3s ease",
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  minWidth: 100,
  fontWeight: 700,
  fontSize: "1rem",
  color: "rgba(255,255,255,0.7)",
  transition: "color 0.3s ease",
  "&:hover": {
    color: "#ffffff",
    opacity: 1,
  },
  "&.Mui-selected": {
    color: "#ffffff",
  },
  "&.Mui-focusVisible": {
    backgroundColor: "rgba(255,255,255,0.3)",
  },
}));

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const StyledText = styled.article`
  font-family: ${theme.fonts.primaryFontBold};
  color: ${COLORS.COLOR_DARK};
  overflow: auto;
`;

const useStyles = makeStyles((themes) => ({
  formLabel: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.COLOR_DARK,
  },
  root: {
    margin: 15,
    [themes.breakpoints.up(640)]: {
      margin: "15px 46px",
    },
    "& .MuiTab-root": {
      padding: "5px 0",
      [themes.breakpoints.up(640)]: {
        padding: "6px 12px",
      },
    },
    "& .MuiTab-wrapper": {
      fontSize: 12,
      [themes.breakpoints.up(640)]: {
        fontSize: 16,
      },
    },
  },

  radioRoot: {
    margin: 15,
    [themes.breakpoints.up(640)]: {
      margin: "0px 26px",
    },
    "& .MuiTab-root": {
      padding: "5px 0",
      [themes.breakpoints.up(640)]: {
        padding: "6px 12px",
      },
    },
    "& .MuiTab-wrapper": {
      fontSize: 12,
      [themes.breakpoints.up(640)]: {
        fontSize: 16,
      },
    },
  },

  appBar: {
    color: COLORS.COLOR_DARK,
    boxShadow: "none",
    background: "transparent",
    fontFamily: theme.fonts.primaryFontBold,
    "& .MuiTabs-flexContainer": {
      justifyContent: "space-between",
      [themes.breakpoints.up(640)]: {
        justifyContent: "flex-start",
      },
    },
  },

  label: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 80,
    [themes.breakpoints.up(400)]: {
      width: "auto",
      maxWidth: 230,
    },
  },
}));

const ManagementTools = () => {
  const classes = useStyles();
  const [bulkUpload, setBulkUpload] = useState(false);
  const [bulkDownload, setBulkDownload] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState(0);
  const themes = useTheme();
  const isBigScreen = useMediaQuery(themes.breakpoints.up(640));
  const [partyMenuItems, setPartyMenuItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const { userObject } = useUser();

  useEffect(() => {
    async function getDocumentTypes() {
      try {
        setLoader({ state: true, message: "Fetching document types..." });
        const response = await CaseService.getDocumentTypes();
        const party_response = await PartyService.getParties(
          "?perPage=1000&status=active&allAgents=true",
        );
        if (response?.documentTypes?.length) {
          setMenuItems(
            response?.documentTypes?.map((item) => ({
              category: item?.documentCategory,
              name: item?.documentType,
            })),
          );
        }
        if (party_response?.data?.length) {
          setPartyMenuItems(
            party_response.data.map(({ name, id, partyCode }) => ({
              label: name,
              value: id,
              partyCode: partyCode,
            })),
          );
          setAgents(
            party_response?.data
              ?.map(({ agents }) => {
                return agents;
              })
              .flat(),
          );
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
    getDocumentTypes(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "dashboard":
        navigate("/dashboard");
        break;
      default:
        break;
    }
  };
  const actions = [
    {
      text: "Bulk Upload",
      props: {
        onClick: () => setBulkUpload(true),
      },
    },
    {
      text: "Bulk Download",
      props: {
        onClick: () => setBulkDownload(true),
      },
      hidden: userObject?.isRequiredChecker,
    },
  ];

  return (
    <div className="paymentHistory">
      <ActionBar
        breadcrumbs={["Dashboard", "Management tools"]}
        {...{ onBreadcrumbClick, actions }}
      />

      <div className={classes.root}>
        <AppBar className={classes.appBar} position="static">
          <StyledTabs
            value={value}
            indicatorColor="primary"
            onChange={(_, newValue) => setValue(newValue)}
            aria-label="pending-action-tab"
          >
            <StyledTab
              label={<div className={classes.label}>{"Rename"}</div>}
              icon={
                !isBigScreen ? (
                  value === 0 ? (
                    <Icon
                      src={require("../../../assets/images/credit-card-active.svg")}
                    />
                  ) : (
                    <Icon
                      src={require("../../../assets/images/credit-card-inactive.svg")}
                    />
                  )
                ) : (
                  ""
                )
              }
              {...a11yProps(0)}
            />
            <StyledTab
              label={<div className={classes.label}>{"Split and upload"}</div>}
              icon={
                !isBigScreen ? (
                  value === 1 ? (
                    <Icon
                      src={require("../../../assets/images/invoice-active.svg")}
                    />
                  ) : (
                    <Icon
                      src={require("../../../assets/images/invoice-inactive.svg")}
                    />
                  )
                ) : (
                  ""
                )
              }
              {...a11yProps(1)}
            />
            {!userObject?.isRequiredChecker ? (
              <StyledTab
                label={
                  <div className={classes.label}>{"Split and download"}</div>
                }
                icon={
                  !isBigScreen ? (
                    value === 2 ? (
                      <Icon
                        src={require("../../../assets/images/refund-active.svg")}
                      />
                    ) : (
                      <Icon
                        src={require("../../../assets/images/refund-inactive.svg")}
                      />
                    )
                  ) : (
                    ""
                  )
                }
                {...a11yProps(2)}
              />
            ) : null}
          </StyledTabs>
        </AppBar>

        <TabPanel value={value} index={0}>
          <Rename {...{ menuItems }} />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <SplitAndUpload {...{ menuItems }} />
        </TabPanel>
        {!userObject?.isRequiredChecker ? (
          <TabPanel value={value} index={2}>
            <SplitAndDownload {...{ menuItems }} />
          </TabPanel>
        ) : null}
      </div>
      {bulkUpload && (
        <BulkUpload
          {...{
            bulkUpload,
            setBulkUpload,
            partyMenuItems,
            agents,
            userObject,
            menuItems,
          }}
        />
      )}
      {bulkDownload && (
        <BulkDownload {...{ bulkDownload, setBulkDownload, menuItems }} />
      )}
    </div>
  );
};

export default ManagementTools;
